import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  MenuItem,
  Tabs,
  Tab,
  LinearProgress,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import profitDistributionService from '../services/profitDistributionService';
import api from '../services/api';

const ProfitDistributionManagement = () => {
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [profitData, setProfitData] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [currentDistribution, setCurrentDistribution] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  
  // Form data
  const [ownerSharePercentage, setOwnerSharePercentage] = useState(40);
  const [reserveFundPercentage, setReserveFundPercentage] = useState(20);
  const [incentivePercentage, setIncentivePercentage] = useState(25);
  const [investmentPercentage, setInvestmentPercentage] = useState(15);
  const [employeeAllocations, setEmployeeAllocations] = useState([]);
  const [notes, setNotes] = useState('');
  
  // Role-based distribution percentages
  const [rolePercentages, setRolePercentages] = useState({
    Director: 25,
    Cinematographer: 20,
    Actor: 20,
    Editor: 15,
    Writer: 10,
    Manager: 5,
    HR: 5
  });
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDistribution, setViewingDistribution] = useState(null);
  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  const [selectedEmployeesToAdd, setSelectedEmployeesToAdd] = useState([]);
  const [employeeSelectionDialogOpen, setEmployeeSelectionDialogOpen] = useState(false);
  const [selectedEligibleEmployees, setSelectedEligibleEmployees] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    setSelectedMonth(getCurrentMonth());
    fetchDistributions();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchProfitData(selectedMonth);
      checkExistingDistribution(selectedMonth);
    }
  }, [selectedMonth]);

  const fetchProfitData = async (month) => {
    try {
      setLoading(true);
      const response = await profitDistributionService.getMonthProfitData(month);
      setProfitData(response.data);
    } catch (error) {
      console.error('Error fetching profit data:', error);
      showAlert('Failed to fetch profit data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributions = async () => {
    try {
      const response = await profitDistributionService.getAllDistributions({ limit: 100 });
      setDistributions(response.data.distributions || []);
    } catch (error) {
      console.error('Error fetching distributions:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/users', { params: { isActive: 'true' } });
      
      // Eligible roles for profit distribution
      const eligibleRoles = ['hr', 'manager', 'actor', 'director', 'editor', 'writer', 'cinematographer'];
      
      const activeEmployees = (response.data.data.users || []).filter(
        emp => emp.isActive && eligibleRoles.includes(emp.role.toLowerCase())
      );
      
      setEmployees(activeEmployees);
      
      // Initialize employee allocations with equal distribution (only for employees not already in allocations)
      if (activeEmployees.length > 0 && employeeAllocations.length === 0) {
        const equalPercentage = (100 / activeEmployees.length).toFixed(2);
        const initialAllocations = activeEmployees.map(emp => ({
          userId: emp.id,
          allocationPercentage: parseFloat(equalPercentage)
        }));
        setEmployeeAllocations(initialAllocations);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const checkExistingDistribution = async (month) => {
    try {
      const response = await profitDistributionService.getDistributionByMonth(month);
      if (response.success) {
        setCurrentDistribution(response.data.distribution);
        populateFormWithDistribution(response.data.distribution);
      }
    } catch (error) {
      // Distribution doesn't exist yet - that's okay
      setCurrentDistribution(null);
    }
  };

  const populateFormWithDistribution = (distribution) => {
    setOwnerSharePercentage(parseFloat(distribution.ownerSharePercentage));
    setReserveFundPercentage(parseFloat(distribution.reserveFundPercentage));
    setIncentivePercentage(parseFloat(distribution.incentivePercentage));
    setInvestmentPercentage(parseFloat(distribution.investmentPercentage));
    setNotes(distribution.notes || '');
    
    // Load role percentages if available
    if (distribution.rolePercentages) {
      setRolePercentages(distribution.rolePercentages);
    }
    
    if (distribution.employeeIncentives && distribution.employeeIncentives.length > 0) {
      const allocations = distribution.employeeIncentives.map(inc => ({
        userId: inc.userId,
        allocationPercentage: parseFloat(inc.allocationPercentage)
      }));
      setEmployeeAllocations(allocations);
    }
  };

  const copyLastDistribution = async () => {
    try {
      setLoading(true);
      const response = await profitDistributionService.getLastDistribution();
      if (response.data.distribution) {
        const lastDist = response.data.distribution;
        setOwnerSharePercentage(parseFloat(lastDist.ownerSharePercentage));
        setReserveFundPercentage(parseFloat(lastDist.reserveFundPercentage));
        setIncentivePercentage(parseFloat(lastDist.incentivePercentage));
        setInvestmentPercentage(parseFloat(lastDist.investmentPercentage));
        
        // Copy role percentages
        if (lastDist.rolePercentages) {
          setRolePercentages(lastDist.rolePercentages);
        }
        
        if (lastDist.employeeIncentives && lastDist.employeeIncentives.length > 0) {
          const allocations = lastDist.employeeIncentives
            .filter(inc => inc.employee.isActive)
            .map(inc => ({
              userId: inc.userId,
              allocationPercentage: parseFloat(inc.allocationPercentage)
            }));
          setEmployeeAllocations(allocations);
        }
        
        showAlert('Copied percentages from last distribution', 'success');
      } else {
        showAlert('No previous distribution found', 'info');
      }
    } catch (error) {
      console.error('Error copying last distribution:', error);
      showAlert('Failed to copy last distribution', 'error');
    } finally {
      setLoading(false);
    }
  };

  const equalDistribution = () => {
    if (employeeAllocations.length > 0) {
      const equalPercentage = (100 / employeeAllocations.length).toFixed(2);
      const allocations = employeeAllocations.map(emp => ({
        ...emp,
        allocationPercentage: parseFloat(equalPercentage)
      }));
      setEmployeeAllocations(allocations);
      showAlert('Applied equal distribution', 'success');
    }
  };

  const getTotalPercentage = () => {
    return parseFloat(ownerSharePercentage) + 
           parseFloat(reserveFundPercentage) + 
           parseFloat(incentivePercentage) + 
           parseFloat(investmentPercentage);
  };

  const getTotalRolePercentage = () => {
    return Object.values(rolePercentages).reduce((sum, val) => sum + parseFloat(val || 0), 0);
  };

  const updateRolePercentage = (role, percentage) => {
    setRolePercentages(prev => ({
      ...prev,
      [role]: parseFloat(percentage) || 0
    }));
  };

  const openEmployeeSelectionDialog = () => {
    // Pre-select all employees
    setSelectedEligibleEmployees(employees.map(emp => emp.id));
    setEmployeeSelectionDialogOpen(true);
  };

  const toggleEmployeeEligibility = (userId) => {
    setSelectedEligibleEmployees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllEligible = () => {
    if (selectedEligibleEmployees.length === employees.length) {
      setSelectedEligibleEmployees([]);
    } else {
      setSelectedEligibleEmployees(employees.map(emp => emp.id));
    }
  };

  const recalculateCurrentEmployees = () => {
    if (employeeAllocations.length === 0) {
      showAlert('No employees in allocation to recalculate', 'warning');
      return;
    }

    // Get currently allocated employees
    const currentEmployeeIds = employeeAllocations.map(a => a.userId);
    const eligibleEmployees = employees.filter(emp => currentEmployeeIds.includes(emp.id));

    // Normalize role name to match rolePercentages keys
    const normalizeRole = (role) => {
      if (!role) return null;
      // Capitalize first letter of each word
      return role
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    // Group employees by role (primary role only)
    const employeesByRole = {};
    eligibleEmployees.forEach(emp => {
      const normalizedRole = normalizeRole(emp.role);
      if (normalizedRole) {
        if (!employeesByRole[normalizedRole]) {
          employeesByRole[normalizedRole] = [];
        }
        employeesByRole[normalizedRole].push(emp);
      }
    });

    // Calculate allocation for each employee based on their role
    const allocations = [];
    let totalCalculated = 0;
    
    Object.keys(rolePercentages).forEach(role => {
      const rolePercentage = rolePercentages[role] || 0;
      const employeesInRole = employeesByRole[role] || [];
      
      if (employeesInRole.length > 0 && rolePercentage > 0) {
        const perEmployeePercentage = rolePercentage / employeesInRole.length;
        employeesInRole.forEach(emp => {
          allocations.push({
            userId: emp.id,
            allocationPercentage: perEmployeePercentage
          });
          totalCalculated += perEmployeePercentage;
        });
      }
    });

    if (allocations.length === 0) {
      showAlert('No employees matched the configured roles. Please check employee role assignments.', 'warning');
      return;
    }

    // Adjust for rounding errors - distribute the difference across all employees
    const difference = 100 - totalCalculated;
    if (Math.abs(difference) > 0.01) {
      const adjustment = difference / allocations.length;
      allocations.forEach(allocation => {
        allocation.allocationPercentage += adjustment;
      });
    }

    // Round all percentages to 2 decimal places
    allocations.forEach(allocation => {
      allocation.allocationPercentage = parseFloat(allocation.allocationPercentage.toFixed(2));
    });

    setEmployeeAllocations(allocations);
    showAlert(`Recalculated allocations for ${allocations.length} employee(s)`, 'success');
  };

  const calculateEmployeeAllocations = () => {
    if (selectedEligibleEmployees.length === 0) {
      showAlert('Please select at least one employee', 'warning');
      return;
    }

    // Filter only selected employees
    const eligibleEmployees = employees.filter(emp => selectedEligibleEmployees.includes(emp.id));

    // Normalize role name to match rolePercentages keys
    const normalizeRole = (role) => {
      if (!role) return null;
      // Capitalize first letter of each word
      return role
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    // Group employees by role (primary role only)
    const employeesByRole = {};
    eligibleEmployees.forEach(emp => {
      const normalizedRole = normalizeRole(emp.role);
      if (normalizedRole) {
        if (!employeesByRole[normalizedRole]) {
          employeesByRole[normalizedRole] = [];
        }
        employeesByRole[normalizedRole].push(emp);
      }
    });

    // Calculate allocation for each employee based on their role
    const allocations = [];
    let totalCalculated = 0;
    
    Object.keys(rolePercentages).forEach(role => {
      const rolePercentage = rolePercentages[role] || 0;
      const employeesInRole = employeesByRole[role] || [];
      
      if (employeesInRole.length > 0 && rolePercentage > 0) {
        const perEmployeePercentage = rolePercentage / employeesInRole.length;
        employeesInRole.forEach(emp => {
          allocations.push({
            userId: emp.id,
            allocationPercentage: perEmployeePercentage
          });
          totalCalculated += perEmployeePercentage;
        });
      }
    });

    if (allocations.length === 0) {
      showAlert('No employees matched the configured roles. Please check employee role assignments.', 'warning');
      return;
    }

    // Adjust for rounding errors - distribute the difference across all employees
    const difference = 100 - totalCalculated;
    if (Math.abs(difference) > 0.01) {
      const adjustment = difference / allocations.length;
      allocations.forEach(allocation => {
        allocation.allocationPercentage += adjustment;
      });
    }

    // Round all percentages to 2 decimal places
    allocations.forEach(allocation => {
      allocation.allocationPercentage = parseFloat(allocation.allocationPercentage.toFixed(2));
    });

    setEmployeeAllocations(allocations);
    setEmployeeSelectionDialogOpen(false);
    showAlert(`Employee allocations calculated for ${allocations.length} employee(s)`, 'success');
  };

  const getTotalEmployeePercentage = () => {
    return employeeAllocations.reduce((sum, emp) => sum + parseFloat(emp.allocationPercentage || 0), 0);
  };

  const updateEmployeeAllocation = (userId, percentage) => {
    setEmployeeAllocations(prev => 
      prev.map(emp => 
        emp.userId === userId 
          ? { ...emp, allocationPercentage: parseFloat(percentage) || 0 }
          : emp
      )
    );
  };

  const removeEmployee = (userId) => {
    setEmployeeAllocations(prev => prev.filter(emp => emp.userId !== userId));
    showAlert('Employee removed from allocation', 'info');
  };

  const getAvailableEmployees = () => {
    const allocatedUserIds = employeeAllocations.map(a => a.userId);
    return employees.filter(emp => !allocatedUserIds.includes(emp.id));
  };

  const handleAddEmployees = () => {
    setSelectedEmployeesToAdd([]);
    setAddEmployeeDialogOpen(true);
  };

  const confirmAddEmployees = () => {
    if (selectedEmployeesToAdd.length === 0) {
      showAlert('Please select at least one employee', 'warning');
      return;
    }

    const newAllocations = selectedEmployeesToAdd.map(userId => ({
      userId,
      allocationPercentage: 0
    }));

    setEmployeeAllocations(prev => [...prev, ...newAllocations]);
    setAddEmployeeDialogOpen(false);
    showAlert(`Added ${selectedEmployeesToAdd.length} employee(s)`, 'success');
  };

  const toggleEmployeeSelection = (userId) => {
    setSelectedEmployeesToAdd(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllAvailable = () => {
    const available = getAvailableEmployees();
    if (selectedEmployeesToAdd.length === available.length) {
      setSelectedEmployeesToAdd([]);
    } else {
      setSelectedEmployeesToAdd(available.map(emp => emp.id));
    }
  };

  const calculateDistribution = async () => {
    try {
      const totalPercentage = getTotalPercentage();
      if (Math.abs(totalPercentage - 100) > 0.01) {
        showAlert(`Total distribution percentage must equal 100%. Current: ${totalPercentage.toFixed(2)}%`, 'error');
        return;
      }

      const totalEmployeePercentage = getTotalEmployeePercentage();
      if (Math.abs(totalEmployeePercentage - 100) > 0.01) {
        showAlert(`Employee allocation percentages must equal 100%. Current: ${totalEmployeePercentage.toFixed(2)}%`, 'error');
        return;
      }

      if (employeeAllocations.length === 0) {
        showAlert('Please add at least one employee to the allocation', 'error');
        return;
      }

      setLoading(true);

      const data = {
        month: selectedMonth,
        ownerSharePercentage,
        reserveFundPercentage,
        incentivePercentage,
        investmentPercentage,
        rolePercentages,
        employeeAllocations,
        notes
      };

      let response;
      if (currentDistribution) {
        response = await profitDistributionService.updateDistribution(currentDistribution.id, data);
      } else {
        response = await profitDistributionService.createDistribution(data);
      }

      showAlert(response.message, 'success');
      // Refetch the distribution to get updated values
      await checkExistingDistribution(selectedMonth);
      await fetchDistributions();
    } catch (error) {
      console.error('Error calculating distribution:', error);
      showAlert(error.response?.data?.message || 'Failed to calculate distribution', 'error');
    } finally {
      setLoading(false);
    }
  };

  const approveDistribution = async () => {
    if (!currentDistribution) return;

    try {
      setLoading(true);
      const response = await profitDistributionService.approveDistribution(currentDistribution.id);
      showAlert('Distribution approved and marked as paid!', 'success');
      setCurrentDistribution(response.data.distribution);
      fetchDistributions();
    } catch (error) {
      console.error('Error approving distribution:', error);
      showAlert(error.response?.data?.message || 'Failed to approve distribution', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDistribution = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteDistribution = async () => {
    if (!currentDistribution) return;

    try {
      setLoading(true);
      await profitDistributionService.cancelDistribution(currentDistribution.id);
      showAlert('Distribution deleted successfully', 'success');
      setDeleteConfirmOpen(false);
      
      // Clear current distribution and reset form
      setCurrentDistribution(null);
      setEmployeeAllocations([]);
      setNotes('');
      
      // Refetch distributions list
      fetchDistributions();
    } catch (error) {
      console.error('Error deleting distribution:', error);
      showAlert(error.response?.data?.message || 'Failed to delete distribution', 'error');
    } finally {
      setLoading(false);
    }
  };

  const viewDistribution = (distribution) => {
    setViewingDistribution(distribution);
    setViewDialogOpen(true);
  };

  const showAlert = (message, severity = 'info') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatMonth = (month) => {
    const [year, monthNum] = month.split('-');
    const date = new Date(year, monthNum - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'default', label: 'Draft' },
      CALCULATED: { color: 'info', label: 'Calculated' },
      APPROVED: { color: 'success', label: 'Approved & Paid' },
    };
    
    const config = statusConfig[status] || statusConfig.DRAFT;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getAllocatedEmployees = () => {
    return employeeAllocations
      .map(allocation => {
        const employee = employees.find(emp => emp.id === allocation.userId);
        return employee ? { ...employee, allocation } : null;
      })
      .filter(Boolean);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Profit Distribution Management
        </Typography>
      </Box>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<CalculateIcon />} label="Calculate Distribution" />
        <Tab icon={<HistoryIcon />} label="Distribution History" />
      </Tabs>

      {activeTab === 0 && (
        <>
          {/* Month Selection and Profit Data */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Select Month"
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {profitData && (
                <>
                  <Grid item xs={12} md={8}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Profit Summary for {formatMonth(selectedMonth)}</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="textSecondary">Total Income</Typography>
                            <Typography variant="h6" color="success.main">{formatCurrency(profitData.totalIncome)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="textSecondary">Total Expenses</Typography>
                            <Typography variant="h6" color="error.main">{formatCurrency(profitData.totalExpenses)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="textSecondary">Net Profit</Typography>
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                              {formatCurrency(profitData.netProfit)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {profitData && (
            <>
              {/* Distribution Percentages */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Profit Distribution Percentages</Typography>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={copyLastDistribution}
                    variant="outlined"
                    size="small"
                    disabled={currentDistribution?.status === 'APPROVED'}
                  >
                    Copy Last Month
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Owner's Share"
                      type="number"
                      value={ownerSharePercentage}
                      onChange={(e) => setOwnerSharePercentage(parseFloat(e.target.value) || 0)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      disabled={currentDistribution?.status === 'APPROVED'}
                    />
                    {profitData && (
                      <Typography variant="caption" color="textSecondary">
                        = {formatCurrency((profitData.netProfit * ownerSharePercentage) / 100)}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Reserve Fund"
                      type="number"
                      value={reserveFundPercentage}
                      onChange={(e) => setReserveFundPercentage(parseFloat(e.target.value) || 0)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      disabled={currentDistribution?.status === 'APPROVED'}
                    />
                    {profitData && (
                      <Typography variant="caption" color="textSecondary">
                        = {formatCurrency((profitData.netProfit * reserveFundPercentage) / 100)}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Employee Incentive"
                      type="number"
                      value={incentivePercentage}
                      onChange={(e) => setIncentivePercentage(parseFloat(e.target.value) || 0)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      disabled={currentDistribution?.status === 'APPROVED'}
                    />
                    {profitData && (
                      <Typography variant="caption" color="textSecondary">
                        = {formatCurrency((profitData.netProfit * incentivePercentage) / 100)}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Company Investment"
                      type="number"
                      value={investmentPercentage}
                      onChange={(e) => setInvestmentPercentage(parseFloat(e.target.value) || 0)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      disabled={currentDistribution?.status === 'APPROVED'}
                    />
                    {profitData && (
                      <Typography variant="caption" color="textSecondary">
                        = {formatCurrency((profitData.netProfit * investmentPercentage) / 100)}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2">
                        Total: <strong>{getTotalPercentage().toFixed(2)}%</strong>
                      </Typography>
                      {Math.abs(getTotalPercentage() - 100) < 0.01 ? (
                        <Chip label="✓ Valid" color="success" size="small" />
                      ) : (
                        <Chip label="Must equal 100%" color="error" size="small" />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Role-Based Distribution Percentages */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Role-Based Incentive Distribution</Typography>
                  <Button
                    startIcon={<CalculateIcon />}
                    onClick={openEmployeeSelectionDialog}
                    variant="outlined"
                    size="small"
                  >
                    Calculate Employee Allocations
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  {Object.keys(rolePercentages).map((role) => {
                    // Normalize role for comparison
                    const normalizeRole = (r) => r?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    const roleEmployeeCount = employees.filter(emp => normalizeRole(emp.role) === role).length;
                    return (
                      <Grid item xs={12} sm={6} md={3} key={role}>
                        <TextField
                          fullWidth
                          label={role}
                          type="number"
                          value={rolePercentages[role]}
                          onChange={(e) => updateRolePercentage(role, e.target.value)}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                          disabled={currentDistribution?.status === 'APPROVED'}
                          size="small"
                        />
                        <Typography variant="caption" color="textSecondary">
                          {roleEmployeeCount} employee(s)
                          {profitData && rolePercentages[role] > 0 && (
                            <> • {formatCurrency((profitData.netProfit * incentivePercentage * rolePercentages[role]) / 10000)}</>
                          )}
                        </Typography>
                      </Grid>
                    );
                  })}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2">
                        Total: <strong>{getTotalRolePercentage().toFixed(2)}%</strong>
                      </Typography>
                      {Math.abs(getTotalRolePercentage() - 100) < 0.01 ? (
                        <Chip label="✓ Valid" color="success" size="small" />
                      ) : (
                        <Chip label="Must equal 100%" color="error" size="small" />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Employee Incentive Allocation */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Employee Incentive Breakdown
                    {profitData && ` (Pool: ${formatCurrency((profitData.netProfit * incentivePercentage) / 100)})`}
                  </Typography>
                  {currentDistribution?.status !== 'APPROVED' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        startIcon={<CalculateIcon />}
                        onClick={recalculateCurrentEmployees}
                        variant="outlined"
                        size="small"
                        disabled={employeeAllocations.length === 0}
                      >
                        Recalculate
                      </Button>
                      <Button
                        startIcon={<PersonAddIcon />}
                        onClick={handleAddEmployees}
                        variant="outlined"
                        size="small"
                        disabled={getAvailableEmployees().length === 0}
                      >
                        Add Employees
                      </Button>
                    </Box>
                  )}
                </Box>

                {employeeAllocations.length === 0 ? (
                  <Alert severity="info">
                    No employees added to allocation yet. Click "Add Employees" to get started.
                  </Alert>
                ) : (
                  <>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Employee ID</TableCell>
                            <TableCell align="right">Allocation %</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            {currentDistribution?.status !== 'APPROVED' && <TableCell align="center">Action</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getAllocatedEmployees().map((employee) => {
                            const percentage = employee.allocation.allocationPercentage;
                            const amount = profitData ? (profitData.netProfit * incentivePercentage * percentage) / 10000 : 0;

                            return (
                              <TableRow key={employee.id}>
                                <TableCell>{employee.fullName}</TableCell>
                                <TableCell>{employee.employeeId}</TableCell>
                                <TableCell align="right">
                                  <TextField
                                    type="number"
                                    value={percentage.toFixed(2)}
                                    onChange={(e) => updateEmployeeAllocation(employee.id, e.target.value)}
                                    size="small"
                                    sx={{ width: 100 }}
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                                    }}
                                    disabled={currentDistribution?.status === 'APPROVED'}
                                  />
                                </TableCell>
                                <TableCell align="right">{formatCurrency(amount)}</TableCell>
                                {currentDistribution?.status !== 'APPROVED' && (
                                  <TableCell align="center">
                                    <Tooltip title="Remove from allocation">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeEmployee(employee.id)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                          <TableRow>
                            <TableCell colSpan={2}><strong>Total</strong></TableCell>
                            <TableCell align="right">
                              <strong>{getTotalEmployeePercentage().toFixed(2)}%</strong>
                              {Math.abs(getTotalEmployeePercentage() - 100) < 0.01 ? (
                                <Chip label="✓" color="success" size="small" sx={{ ml: 1 }} />
                              ) : (
                                <Chip label="!" color="error" size="small" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <strong>
                                {profitData && formatCurrency((profitData.netProfit * incentivePercentage) / 100)}
                              </strong>
                            </TableCell>
                            {currentDistribution?.status !== 'APPROVED' && <TableCell />}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Paper>

              {/* Notes and Actions */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={currentDistribution?.status === 'APPROVED'}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  {currentDistribution && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteDistribution}
                      disabled={loading}
                    >
                      Delete Distribution
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<CalculateIcon />}
                    onClick={calculateDistribution}
                    disabled={loading}
                  >
                    {currentDistribution ? 'Update' : 'Save'} Distribution
                  </Button>
                </Box>
              </Paper>
            </>
          )}
        </>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Distribution History</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Profit</TableCell>
                  <TableCell align="right">Owner Share</TableCell>
                  <TableCell align="right">Reserve Fund</TableCell>
                  <TableCell align="right">Incentives</TableCell>
                  <TableCell align="right">Investment</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distributions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary">No distributions found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  distributions.map((dist) => (
                    <TableRow key={dist.id}>
                      <TableCell>{formatMonth(dist.month)}</TableCell>
                      <TableCell align="right">{formatCurrency(dist.netProfit)}</TableCell>
                      <TableCell align="right">{formatCurrency(dist.ownerShareAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(dist.reserveFundAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(dist.totalIncentivePool)}</TableCell>
                      <TableCell align="right">{formatCurrency(dist.investmentAmount)}</TableCell>
                      <TableCell align="center">{getStatusChip(dist.status)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => viewDistribution(dist)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Distribution">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => {
                              setCurrentDistribution(dist);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* View Distribution Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Distribution Details - {viewingDistribution && formatMonth(viewingDistribution.month)}
        </DialogTitle>
        <DialogContent>
          {viewingDistribution && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Total Income</Typography>
                  <Typography variant="h6">{formatCurrency(viewingDistribution.totalIncome)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Total Expenses</Typography>
                  <Typography variant="h6">{formatCurrency(viewingDistribution.totalExpenses)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Net Profit</Typography>
                  <Typography variant="h5" color="primary">{formatCurrency(viewingDistribution.netProfit)}</Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Distribution Breakdown</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Owner's Share</TableCell>
                      <TableCell align="right">{viewingDistribution.ownerSharePercentage}%</TableCell>
                      <TableCell align="right">{formatCurrency(viewingDistribution.ownerShareAmount)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Reserve Fund</TableCell>
                      <TableCell align="right">{viewingDistribution.reserveFundPercentage}%</TableCell>
                      <TableCell align="right">{formatCurrency(viewingDistribution.reserveFundAmount)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Employee Incentives</TableCell>
                      <TableCell align="right">{viewingDistribution.incentivePercentage}%</TableCell>
                      <TableCell align="right">{formatCurrency(viewingDistribution.totalIncentivePool)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Company Investment</TableCell>
                      <TableCell align="right">{viewingDistribution.investmentPercentage}%</TableCell>
                      <TableCell align="right">{formatCurrency(viewingDistribution.investmentAmount)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {viewingDistribution.employeeIncentives && viewingDistribution.employeeIncentives.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Employee Incentives</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell align="right">Allocation %</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {viewingDistribution.employeeIncentives.map((inc) => (
                          <TableRow key={inc.id}>
                            <TableCell>{inc.employee?.fullName}</TableCell>
                            <TableCell align="right">{inc.allocationPercentage}%</TableCell>
                            <TableCell align="right">{formatCurrency(inc.incentiveAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {viewingDistribution.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">Notes:</Typography>
                  <Typography variant="body1">{viewingDistribution.notes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Employee Selection Dialog for Calculate Allocations */}
      <Dialog open={employeeSelectionDialogOpen} onClose={() => setEmployeeSelectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Eligible Employees for Incentive</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select employees who are eligible for incentive distribution this month
            </Alert>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={selectedEligibleEmployees.length === employees.length}
                indeterminate={
                  selectedEligibleEmployees.length > 0 &&
                  selectedEligibleEmployees.length < employees.length
                }
                onChange={selectAllEligible}
              />
              <Typography variant="body2">
                Select All ({selectedEligibleEmployees.length} of {employees.length} selected)
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow
                      key={employee.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedEligibleEmployees.includes(employee.id)}
                          onChange={() => toggleEmployeeEligibility(employee.id)}
                        />
                      </TableCell>
                      <TableCell onClick={() => toggleEmployeeEligibility(employee.id)}>{employee.fullName}</TableCell>
                      <TableCell onClick={() => toggleEmployeeEligibility(employee.id)}>{employee.employeeId}</TableCell>
                      <TableCell onClick={() => toggleEmployeeEligibility(employee.id)}>{employee.role}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmployeeSelectionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={calculateEmployeeAllocations}
            variant="contained"
            disabled={selectedEligibleEmployees.length === 0}
          >
            Calculate for Selected ({selectedEligibleEmployees.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Employees Dialog */}
      <Dialog open={addEmployeeDialogOpen} onClose={() => setAddEmployeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Employees to Allocation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {getAvailableEmployees().length === 0 ? (
              <Alert severity="info">All eligible employees are already in the allocation.</Alert>
            ) : (
              <>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedEmployeesToAdd.length === getAvailableEmployees().length}
                    indeterminate={
                      selectedEmployeesToAdd.length > 0 &&
                      selectedEmployeesToAdd.length < getAvailableEmployees().length
                    }
                    onChange={selectAllAvailable}
                  />
                  <Typography variant="body2">
                    Select All ({selectedEmployeesToAdd.length} of {getAvailableEmployees().length} selected)
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Employee ID</TableCell>
                        <TableCell>Role</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getAvailableEmployees().map((employee) => (
                        <TableRow
                          key={employee.id}
                          hover
                          onClick={() => toggleEmployeeSelection(employee.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedEmployeesToAdd.includes(employee.id)}
                              onChange={() => toggleEmployeeSelection(employee.id)}
                            />
                          </TableCell>
                          <TableCell>{employee.fullName}</TableCell>
                          <TableCell>{employee.employeeId}</TableCell>
                          <TableCell>{employee.role}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEmployeeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmAddEmployees}
            variant="contained"
            disabled={selectedEmployeesToAdd.length === 0}
          >
            Add Selected ({selectedEmployeesToAdd.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the profit distribution for {currentDistribution && formatMonth(currentDistribution.month)}?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteDistribution} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}
    </Container>
  );
};

export default ProfitDistributionManagement;
